import axios from "axios";
import localStorageKey from "../../enumerations/localStorageKey";
import { envChangeType } from "../../plugins/utils";

const THEATER_API_ORIGIN = "https://tv.redbeantv.com";

const LANG =
  localStorage.getItem(localStorageKey.LANG_SETTING) || envChangeType("REACT_APP_DEFAULT_LANG");

// The API caps the dedupe window at 2000 drama ids.
export const EXCLUDE_DRAMA_ID_LIMIT = 2000;

const getDeviceId = () => {
  const key = "theater_device_id";
  const existing = window.localStorage.getItem(key);
  if (existing) return existing;
  const deviceId = `dm${Date.now().toString(36)}${Math.random().toString(36).slice(2, 10)}`;
  window.localStorage.setItem(key, deviceId);
  return deviceId;
};

// Redbean is a third party and does not share accounts with us, so our own
// login token must never be forwarded here. All theater traffic is guest
// traffic identified by device_id.
const theaterRequest = (method, path, data = {}) => {
  return axios({
    method,
    url: `${THEATER_API_ORIGIN}${path}`,
    ...(method.toLowerCase() === "get" ? { params: data } : { data }),
  });
};

// Theater responses are wrapped as { code, msg, time, data }; code 1 is success.
export const unwrapTheaterData = (res) => {
  const body = res?.data || {};
  if (body.code !== 1) {
    throw new Error(body.msg || "theater request failed");
  }
  return body.data;
};

export const apiGetTheaterHome502 = (params = {}) => {
  return theaterRequest("get", "/api/home/home502", {
    lang: "zh",
    display_lang: LANG,
    device_id: getDeviceId(),
    ...params,
  });
};

export const apiGetRandomDramaList = ({ excludeDramaIds = [], ...params } = {}) => {
  return theaterRequest("get", "/api/drama/randomDramaList", {
    page: 1,
    lang: "zh",
    display_lang: LANG,
    device_id: getDeviceId(),
    ...(excludeDramaIds.length
      ? { exclude_drama_ids: excludeDramaIds.slice(-EXCLUDE_DRAMA_ID_LIMIT).join(",") }
      : {}),
    ...params,
  });
};

// Playback urls are signed and expire in about an hour, so a feed item that has
// been on screen for a while needs a freshly issued one.
export const apiGetPlayUrl = ({ episode_id }) => {
  return theaterRequest("get", "/api/drama/getPlayUrl", {
    episode_id,
    device_id: getDeviceId(),
  });
};

export const apiGetDramaList = (params = {}) => {
  return theaterRequest("get", "/api/drama/getDramaList", {
    page: 1,
    lang: "zh",
    display_lang: LANG,
    device_id: getDeviceId(),
    ...params,
  });
};

export const apiGetTheaterCategories = (params = {}) => {
  return theaterRequest("get", "/api/home/getCategories", {
    lang: "zh",
    display_lang: LANG,
    device_id: getDeviceId(),
    ...params,
  });
};

export const apiGetTheaterRankings = (params = {}) => {
  return theaterRequest("get", "/api/home/getRankings", {
    lang: "zh",
    display_lang: LANG,
    device_id: getDeviceId(),
    type: "hot",
    ...params,
  });
};

// Needs a Redbean account token, which we do not have while accounts are not
// linked; the like button in ShortVideoItem stays disabled until they are.
export const apiToggleDramaLikes = ({ episode_id }) => {
  return theaterRequest("post", "/api/drama/toggleLikes", { episode_id });
};

export const resolveTheaterAssetUrl = (path) => {
  if (!path) return "";
  if (/^https?:\/\//i.test(path)) return path;
  return `${THEATER_API_ORIGIN}${path.startsWith("/") ? "" : "/"}${path}`;
};
